import React, { useState, useEffect } from "react";
import { FiX, FiPlus, FiTrash2, FiMove } from "react-icons/fi";
import {
  FIELD_TYPE_MAP,
  FIELD_TYPES,
  isLockedFormField,
  getPlanServiceInterestOptions,
  NEUTERED_STATUS_OPTIONS,
} from "./boardingFormTemplate";
import { FIELD_SAVE_AS_OPTIONS, normalizeMapsTo } from "./formFieldCatalog";
import { useProfile } from "../../context/ProfileContext";

const CHOICE_TYPES = [
  "dropdown",
  "single_choice",
  "multiple_choice",
  "checkbox",
  "yes_no",
  "vaccination_details",
];

const FIELD_TYPE_GROUPS = [...new Set(FIELD_TYPES.map((f) => f.group))];

// ─── Option List Editor ──────────────────────────────────────────────────────

function OptionListEditor({ options, onChange, disabled = false }) {
  const addOption = () => onChange([...options, { label: "", value: "" }]);
  const removeOption = (i) => onChange(options.filter((_, idx) => idx !== i));
  const updateOption = (i, val) => {
    const next = [...options];
    next[i] = { label: val, value: val };
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-2">
          <FiMove className="text-gray-400 cursor-grab" />
          <input
            type="text"
            value={opt.label || opt.value || ""}
            onChange={(e) => updateOption(i, e.target.value)}
            placeholder={`Option ${i + 1}`}
            disabled={disabled}
            readOnly={disabled}
            className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
          />
          <button
            type="button"
            onClick={() => removeOption(i)}
            disabled={disabled || options.length <= 1}
            className="text-red-400 hover:text-red-600 disabled:opacity-30"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={addOption}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-1"
        >
          <FiPlus className="w-4 h-4" /> Add Option
        </button>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function FieldConfigDrawer({ isOpen, onClose, field, onSave }) {
  const [config, setConfig] = useState(null);
    const { profileData } = useProfile();


 useEffect(() => {
   if (!isOpen || !field) return;

   const mapsToNorm = normalizeMapsTo(
     field.mapsTo || field.maps_to || "custom",
   );
   const isServicesField = mapsToNorm === "customer.services_interested_in";
   const isNeuteredField =
     mapsToNorm === "pet.neutered_status" ||
     String(field.label || "").trim().toLowerCase() === "neutered / spayed status";

   let nextOptions =
     Array.isArray(field.options) && field.options.length > 0
       ? field.options
       : [];

   if (isServicesField) {
     const allowed = getPlanServiceInterestOptions(profileData); 
     nextOptions = nextOptions.filter((o) =>
       allowed.includes(o.label || o.value),
     );
     if (nextOptions.length === 0) {
       nextOptions = allowed.map((s) => ({ label: s, value: s }));
     }
   } else if (isNeuteredField) {
     const hasLegacyYesNo = nextOptions.some((o) => {
       const val = String(o.label || o.value || "").trim().toLowerCase();
       return val === "yes" || val === "no";
     });
     if (nextOptions.length === 0 || hasLegacyYesNo) {
       nextOptions = (NEUTERED_STATUS_OPTIONS || [
         "Not Neutered",
         "Neutered",
         "Unknown",
       ]).map((s) => ({ label: s, value: s }));
     }
   }

   setConfig({
     label: field.label || "",
     fieldType: field.fieldType || "short_text",
     mapsTo: mapsToNorm,
     placeholder:
       field.placeholder ||
       (isNeuteredField ? "Select Neutered Status" : ""),
     helpText: field.helpText || "",
     defaultValue: field.defaultValue || "",
     required: field.required || false,
     enabled: field.enabled !== false,
     searchable: field.searchable || false,
     options: nextOptions,
     validation: field.validation || {},
   });
 }, [isOpen, field, profileData]);

  if (!isOpen || !config) return null;

  const locked = isLockedFormField(field);

  const set = (key, val) => {
    if (locked) return;
    setConfig((prev) => ({ ...prev, [key]: val }));
  };
  const setValidation = (key, val) => {
    if (locked) return;
    setConfig((prev) => ({
      ...prev,
      validation: { ...prev.validation, [key]: val },
    }));
  };

  const changeFieldType = (nextType) => {
    if (locked) return;
    setConfig((prev) => {
      const next = { ...prev, fieldType: nextType };
      if (CHOICE_TYPES.includes(nextType)) {
        const hasRealOptions = (prev.options || []).some((o) =>
          (o.label || o.value || "").trim(),
        );
        if (!hasRealOptions) {
          const isNeuteredField =
            config.mapsTo === "pet.neutered_status" ||
            String(config.label || "").trim().toLowerCase() === "neutered / spayed status";
          if (isNeuteredField) {
            next.options = (NEUTERED_STATUS_OPTIONS || [
              "Not Neutered",
              "Neutered",
              "Unknown",
            ]).map((s) => ({ label: s, value: s }));
          } else {
            next.options =
              nextType === "yes_no"
                ? [
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                  ]
                : [
                    { label: "", value: "" },
                    { label: "", value: "" },
                  ];
          }
        }
      }
      return next;
    });
  };

  const hasOptions = CHOICE_TYPES.includes(config.fieldType);
  const isFileType = ["file_upload", "image_upload", "multi_file_upload"].includes(
    config.fieldType,
  );
  const isDateType = ["date_picker", "datetime_picker"].includes(config.fieldType);
  const isDescType = ["description_text", "section_heading"].includes(
    config.fieldType,
  );

  const handleSave = () => {
    if (!config.label.trim()) return;

    const nextMaps = config.mapsTo || "custom";
    const nextUid =
      nextMaps !== "custom" && nextMaps !== "section"
        ? nextMaps
        : field.fieldUid || field.field_uid || field.id;

    onSave({
      ...field,
      ...config,
      mapsTo: nextMaps,
      maps_to: nextMaps,
      fieldUid: nextUid,
      field_uid: nextUid,
    });
  };

  const fieldTypeMeta = FIELD_TYPE_MAP[config.fieldType];

  return (
    <div className="fixed inset-0 z-[70] flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full sm:w-[480px] bg-white h-full flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <h3 className="text-base font-semibold text-gray-800">
              Configure Field
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {fieldTypeMeta?.label || config.fieldType}
              {locked ? " · System field" : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-200 text-gray-500 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {locked && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              This is a required system field. Label, type, and options cannot
              be changed.
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Field Type
            </label>
            <select
              value={config.fieldType}
              onChange={(e) => changeFieldType(e.target.value)}
              disabled={locked}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            >
              {FIELD_TYPE_GROUPS.map((group) => (
                <optgroup key={group} label={group}>
                  {FIELD_TYPES.filter((ft) => ft.group === group).map((ft) => (
                    <option key={ft.value} value={ft.value}>
                      {ft.label}
                    </option>
                  ))}
                </optgroup>
              ))}
              {!FIELD_TYPE_MAP[config.fieldType] && (
                <option value={config.fieldType}>{config.fieldType}</option>
              )}
            </select>
          </div>

          {/* Label */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Field Label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.label}
              onChange={(e) => set("label", e.target.value)}
              readOnly={locked}
              disabled={locked}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="Enter field label"
            />
          </div>

          {/* Save as — data destination */}
          {/* {!isDescType && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Save as
              </label>
              <select
                value={config.mapsTo}
                onChange={(e) => set("mapsTo", e.target.value)}
                disabled={locked}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                {["Custom", "Customer", "Pet", "Consent"].map((group) => {
                  const opts = FIELD_SAVE_AS_OPTIONS.filter(
                    (o) => o.group === group,
                  );
                  if (!opts.length) return null;
                  return (
                    <optgroup key={group} label={group}>
                      {opts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
                {!FIELD_SAVE_AS_OPTIONS.some(
                  (o) => o.value === config.mapsTo,
                ) && (
                  <option value={config.mapsTo}>{config.mapsTo}</option>
                )}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">
                Which customer / pet field this answer should be saved into.
              </p>
            </div>
          )} */}

          {/* Description / Heading content */}
          {isDescType && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Content
              </label>
              <textarea
                value={config.defaultValue}
                onChange={(e) => set("defaultValue", e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
                placeholder="Enter the text content…"
              />
            </div>
          )}

          {/* Required toggle */}
          {!isDescType && (
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700">Required</p>
                <p className="text-xs text-gray-400">
                  Respondents must answer this field
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!locked) set("required", !config.required);
                }}
                disabled={locked}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.required ? "bg-blue-600" : "bg-gray-300"
                } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
                    config.required ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          )}

          {/* Searchable — dropdowns */}
          {(config.fieldType === "dropdown" ||
            config.fieldType === "multiple_choice") && (
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700">Searchable</p>
                <p className="text-xs text-gray-400">
                  Allow searching within options
                </p>
              </div>
              <button
                type="button"
                onClick={() => set("searchable", !config.searchable)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.searchable ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
                    config.searchable ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          )}

          {/* Options editor */}
          {hasOptions && (
            <div className="border-t border-gray-100 pt-3">
              <label className="block text-xs font-semibold text-gray-700 mb-2">
                Options
              </label>
              <OptionListEditor
                options={
                  config.options.length > 0
                    ? config.options
                    : [{ label: "", value: "" }]
                }
                onChange={(opts) => set("options", opts)}
                disabled={locked}
              />
            </div>
          )}

          {/* File validation */}
          {isFileType && (
            <div className="border-t border-gray-100 pt-3 space-y-3">
              <p className="text-xs font-semibold text-gray-700">
                File Validation
              </p>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Accepted File Types
                </label>
                <input
                  type="text"
                  value={config.validation?.accept || ""}
                  onChange={(e) => setValidation("accept", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder=".pdf,.jpg,.jpeg,.png"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Max File Size (MB)
                </label>
                <input
                  type="number"
                  value={config.validation?.maxSizeMB || ""}
                  onChange={(e) =>
                    setValidation("maxSizeMB", Number(e.target.value))
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="10"
                  min={1}
                  max={100}
                />
              </div>
            </div>
          )}

          {/* Phone validation */}
          {config.fieldType === "phone" && (
            <div className="border-t border-gray-100 pt-3 space-y-3">
              <p className="text-xs font-semibold text-gray-700">
                Phone Validation
              </p>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Pattern (regex)
                </label>
                <input
                  type="text"
                  value={config.validation?.pattern || ""}
                  onChange={(e) => setValidation("pattern", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                  placeholder="^[6-9][0-9]{9}$"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Default: 10-digit Indian mobile number validation
                </p>
              </div>
            </div>
          )}

          {/* Date validation */}
          {isDateType && (
            <div className="border-t border-gray-100 pt-3 space-y-3">
              <p className="text-xs font-semibold text-gray-700">
                Date Constraints
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Min Date
                  </label>
                  <input
                    type="date"
                    value={config.validation?.minDate || ""}
                    onChange={(e) => setValidation("minDate", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Max Date
                  </label>
                  <input
                    type="date"
                    value={config.validation?.maxDate || ""}
                    onChange={(e) => setValidation("maxDate", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Date Format
                </label>
                <select
                  value={config.validation?.format || "DD/MM/YYYY"}
                  onChange={(e) => setValidation("format", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                >
                  <option>DD/MM/YYYY</option>
                  <option>MM/DD/YYYY</option>
                  <option>YYYY-MM-DD</option>
                </select>
              </div>
            </div>
          )}

          {/* Number validation */}
          {config.fieldType === "number" && (
            <div className="border-t border-gray-100 pt-3 space-y-3">
              <p className="text-xs font-semibold text-gray-700">
                Number Constraints
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Min Value
                  </label>
                  <input
                    type="number"
                    value={config.validation?.min ?? ""}
                    onChange={(e) => setValidation("min", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Max Value
                  </label>
                  <input
                    type="number"
                    value={config.validation?.max ?? ""}
                    onChange={(e) => setValidation("max", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Min Length
                  </label>
                  <input
                    type="number"
                    value={config.validation?.minLength ?? ""}
                    onChange={(e) =>
                      setValidation("minLength", Number(e.target.value))
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Max Length
                  </label>
                  <input
                    type="number"
                    value={config.validation?.maxLength ?? ""}
                    onChange={(e) =>
                      setValidation("maxLength", Number(e.target.value))
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-5 py-4 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {locked ? "Close" : "Cancel"}
          </button>
          {!locked && (
            <button
              onClick={handleSave}
              disabled={!config.label.trim()}
              className="px-5 py-2 text-sm font-semibold bg-[#1B4582] text-white rounded-lg hover:bg-[#163a6e] disabled:opacity-50 transition-colors"
            >
              Save Field
            </button>
          )}
        </div>
      </div>
    </div>
  );
}